import { ReactElement } from 'react';
import { OrderDetailsI } from '@app/healthcheck/order/types';
import {
  BookOpenIconNew,
  CalendarIconNew,
  ClockIcon,
  DocumentIcon,
  EditPenIcon,
  EmailIconNew,
  HashIcon,
  HealthIconNew,
  UserIconNew,
} from '@assets/images';
import Tooltip from '@app/common/Tooltip/Tooltip';
import Lang from '@lang/en';
import {
  CardLinkParagraph,
  CardParagraph,
  StyledCard,
  StyledHeader,
  WrapHeader,
  WrapperCard,
} from './styles';
import { getTestTypeLabel } from '../../../ordersTable/utils';
import {
  applyEllipsis,
  dateTime24FriendlyFormatWithComma,
  formatUTCStringAsApiTimestamp,
} from '@app/utils';
import Button from '@designSystem/buttons/index';

interface PersonProps {
  data: OrderDetailsI;
  handleOpenEditPersonal: () => void;
  handleOpenEditOrder: () => void;
  isOrderStatusDone?: boolean;
}

const DetailsPanelMobile = ({
  data,
  handleOpenEditPersonal,
  handleOpenEditOrder,
  isOrderStatusDone,
}: PersonProps): ReactElement => {
  const bookedDate = formatUTCStringAsApiTimestamp(
    data.bookedDate,
    dateTime24FriendlyFormatWithComma
  );

  return (
    <WrapperCard>
      <StyledCard>
        <WrapHeader>
          <StyledHeader>{Lang.healthCheckDetailsOrderCard}</StyledHeader>
          {!isOrderStatusDone && (
            <Button
              data-testid="healthcheck-order-edit-order-button"
              type="tertiary"
              label={Lang.editText}
              icon={<EditPenIcon />}
              onClick={handleOpenEditOrder}
            />
          )}
        </WrapHeader>
        <Tooltip
          textTooltip={Lang.healthCheckOrderTestTypeTool}
          followingTooltip={true}
          position="bottom-start"
          arrow={false}
          theme="testedme_dark"
        >
          <CardParagraph data-testid="healthcheck-order-details-testType">
            <HealthIconNew />
            {getTestTypeLabel(data.testType, data.testCategory)}
          </CardParagraph>
        </Tooltip>

        <Tooltip
          textTooltip={Lang.healthCheckOrderTimeTool}
          followingTooltip={true}
          position="bottom-start"
          arrow={false}
          theme="testedme_dark"
        >
          <CardParagraph data-testid="healthcheck-order-details-bookedDate">
            <ClockIcon />
            {bookedDate}
          </CardParagraph>
        </Tooltip>
        <Tooltip
          textTooltip={Lang.healthCheckEditOrderOtherRefNumberLabel}
          followingTooltip={true}
          position="bottom-start"
          arrow={false}
          theme="testedme_dark"
        >
          <CardParagraph data-testid="healthcheck-order-details-referenceCode">
            <DocumentIcon />
            {data.referenceCode}
          </CardParagraph>
        </Tooltip>
        <Tooltip
          textTooltip={Lang.healthCheckEditOrderOtherBarcodeNumberLabel}
          followingTooltip={true}
          position="bottom-start"
          arrow={false}
          theme="testedme_dark"
        >
          <CardParagraph data-testid="healthcheck-order-details-barcodeNumber">
            <HashIcon />
            {applyEllipsis(String(data.barcodeNumber), 28)}
          </CardParagraph>
        </Tooltip>
      </StyledCard>

      <StyledCard>
        <WrapHeader>
          <StyledHeader>{Lang.healthCheckDetailsPersonalCard}</StyledHeader>
          <Button
            data-testid="healthcheck-order-edit-personal-button"
            type="tertiary"
            label={Lang.editText}
            icon={<EditPenIcon />}
            onClick={handleOpenEditPersonal}
          />
        </WrapHeader>
        <Tooltip
          textTooltip={Lang.healthCheckOrderPersonalFullNameTool}
          followingTooltip={true}
          position="bottom-start"
          arrow={false}
          theme="testedme_dark"
        >
          <CardParagraph data-testid="healthcheck-order-details-fullName">
            <UserIconNew />
            {applyEllipsis(`${data.firstName} ${data.lastName}`, 29)}
          </CardParagraph>
        </Tooltip>
        <Tooltip
          textTooltip={Lang.healthCheckEditOrderBirthdateLabel}
          followingTooltip={true}
          position="bottom-start"
          arrow={false}
          theme="testedme_dark"
        >
          <CardParagraph data-testid="healthcheck-order-details-birthday">
            <CalendarIconNew />
            {data.birthdate}
          </CardParagraph>
        </Tooltip>
        <Tooltip
          textTooltip={Lang.healthCheckOrderPersonalDocumentsTool}
          followingTooltip={true}
          position="bottom-start"
          arrow={false}
          theme="testedme_dark"
        >
          <CardParagraph data-testid="healthcheck-order-details-document">
            <BookOpenIconNew />
            {`${Lang.healthCheckOrderPersonalDocumentsLabel} ${data.passportNumber}`}
          </CardParagraph>
        </Tooltip>
        <Tooltip
          textTooltip={Lang.healthCheckOrderPersonalEmailTool}
          followingTooltip={true}
          position="bottom-start"
          arrow={false}
          theme="testedme_dark"
        >
          <CardLinkParagraph
            data-testid="healthcheck-order-details-email"
            href={`mailto:${data.email}`}
            rel="noopener noreferrer"
          >
            <EmailIconNew />
            {applyEllipsis(data.email, 33)}
          </CardLinkParagraph>
        </Tooltip>
      </StyledCard>
    </WrapperCard>
  );
};

export default DetailsPanelMobile;
